import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  Input,
  NativeSelect,
} from '@tyndo/ui-kit';
import { Search, Plus, LayoutGrid } from 'lucide-react';
import type { WidgetDefinition } from './types';

interface WidgetGalleryProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  definitions: WidgetDefinition[];
  onAdd: (widget: WidgetDefinition) => void;
  /** Loading state — definitions still being fetched. */
  loading?: boolean;
  /** Error string from the definitions fetch, if any. */
  error?: string | null;
}

const CATEGORY_ORDER: ReadonlyArray<string> = [
  'kpi',
  'queue',
  'trend',
  'layout',
  'overview',
  'health',
  'action',
  'evidence',
  'metrics',
  'other',
];

const CATEGORY_LABEL: Record<string, string> = {
  kpi: 'KPIs',
  queue: 'Queues',
  trend: 'Trends',
  layout: 'Layouts',
  overview: 'Overviews',
  health: 'Health',
  action: 'Actions',
  evidence: 'Evidence',
  metrics: 'Metrics',
  other: 'Other',
};

/**
 * Modal gallery of registered widgets. Opens from the "Add widget" button
 * in the editor toolbar. Renders categorised cards filtered by a free-text
 * search box plus persona / industry / module dropdowns.
 *
 * Accessibility:
 *   - Wraps Radix Dialog which provides focus-trap + Escape-to-close.
 *   - The result grid is an aria `listbox`; each card is an `option` with an
 *     aria-label including the widget label, category, and default size.
 *   - Roving tabindex: ArrowUp/Down/Left/Right move focus between cards within
 *     a single tab stop, Home/End jump to the first/last result, Enter/Space
 *     adds the focused widget, and Escape closes (via the Dialog).
 *   - A polite live region announces the filtered result count so screen-reader
 *     users hear how their filter changes narrowed the catalogue.
 */
const WidgetGallery: React.FC<WidgetGalleryProps> = ({
  open,
  onOpenChange,
  definitions,
  onAdd,
  loading = false,
  error = null,
}) => {
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState<string>('');
  const [module, setModule] = useState<string>('');
  const [persona, setPersona] = useState<string>('');
  const [industry, setIndustry] = useState<string>('');

  const moduleOptions = useMemo(() => {
    const set = new Set<string>();
    definitions.forEach((d) => d.moduleId && set.add(d.moduleId));
    return [...set].sort();
  }, [definitions]);

  const personaOptions = useMemo(() => {
    const set = new Set<string>();
    definitions.forEach((d) => d.personaDefaults?.forEach((p) => set.add(p)));
    return [...set].sort();
  }, [definitions]);

  const industryOptions = useMemo(() => {
    const set = new Set<string>();
    definitions.forEach((d) => d.industryDefaults?.forEach((i) => set.add(i)));
    return [...set].sort();
  }, [definitions]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return definitions.filter((d) => {
      if (q && !`${d.label} ${d.description ?? ''} ${d.widgetId}`.toLowerCase().includes(q)) {
        return false;
      }
      if (category && (d.category ?? 'other') !== category) return false;
      if (module && d.moduleId !== module) return false;
      if (persona && !d.personaDefaults?.includes(persona)) return false;
      if (industry && !d.industryDefaults?.includes(industry)) return false;
      return true;
    });
  }, [definitions, query, category, module, persona, industry]);

  const grouped = useMemo(() => {
    const buckets = new Map<string, WidgetDefinition[]>();
    filtered.forEach((w) => {
      const key = (w.category ?? 'other').toLowerCase();
      if (!buckets.has(key)) buckets.set(key, []);
      buckets.get(key)!.push(w);
    });
    // Sort categories by canonical order, with unknown categories last.
    return [...buckets.entries()].sort(([a], [b]) => {
      const ai = CATEGORY_ORDER.indexOf(a);
      const bi = CATEGORY_ORDER.indexOf(b);
      const ax = ai === -1 ? CATEGORY_ORDER.length : ai;
      const bx = bi === -1 ? CATEGORY_ORDER.length : bi;
      return ax - bx;
    });
  }, [filtered]);

  // Flattened, group-ordered widget list so roving keyboard navigation can move
  // through the rendered cards in visual order regardless of grouping.
  const flatWidgets = useMemo(() => grouped.flatMap(([, widgets]) => widgets), [grouped]);

  // Roving tabindex: one element in the result listbox is tabbable at a time;
  // arrow keys move the active index. Clamp/reset whenever the filtered set
  // changes so the active index never points past the end.
  const [activeIndex, setActiveIndex] = useState(0);
  const cardRefs = useRef<Array<HTMLButtonElement | null>>([]);
  useEffect(() => {
    setActiveIndex((i) => (flatWidgets.length === 0 ? 0 : Math.min(i, flatWidgets.length - 1)));
  }, [flatWidgets.length]);

  // Roughly how many cards sit per row at the widest breakpoint (lg:grid-cols-3)
  // so Up/Down move a row at a time; falls back gracefully on narrow layouts.
  const COLS = 3;

  const focusCard = (index: number) => {
    const clamped = Math.max(0, Math.min(index, flatWidgets.length - 1));
    setActiveIndex(clamped);
    cardRefs.current[clamped]?.focus();
  };

  const handleGridKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (flatWidgets.length === 0) return;
    switch (e.key) {
      case 'ArrowRight':
        e.preventDefault();
        focusCard(activeIndex + 1);
        break;
      case 'ArrowLeft':
        e.preventDefault();
        focusCard(activeIndex - 1);
        break;
      case 'ArrowDown':
        e.preventDefault();
        focusCard(activeIndex + COLS);
        break;
      case 'ArrowUp':
        e.preventDefault();
        focusCard(activeIndex - COLS);
        break;
      case 'Home':
        e.preventDefault();
        focusCard(0);
        break;
      case 'End':
        e.preventDefault();
        focusCard(flatWidgets.length - 1);
        break;
      default:
        break;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="glass-strong max-w-5xl max-h-[85vh] overflow-hidden flex flex-col rounded-3xl"
        data-testid="widget-gallery"
      >
        <DialogHeader>
          <DialogTitle>Add a widget</DialogTitle>
          <DialogDescription id="widget-gallery-help">
            Choose from {definitions.length} registered widgets. Use the filters to narrow by
            category, module, persona, or industry. Use the arrow keys to move between widget
            cards and Enter to add one.
          </DialogDescription>
        </DialogHeader>

        {/* Polite live region — announces how filtering narrowed the catalogue
            so screen-reader users hear the result count change. */}
        <p className="sr-only" role="status" aria-live="polite" aria-atomic="true">
          {loading
            ? 'Loading widget catalogue'
            : error
              ? `Error: ${error}`
              : `${filtered.length} widget${filtered.length === 1 ? '' : 's'} match the current filters`}
        </p>

        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-2 py-2">
          <div className="relative md:col-span-1">
            <Search
              aria-hidden="true"
              className="absolute left-2 top-2.5 h-3.5 w-3.5 text-muted-foreground"
            />
            <Input
              aria-label="Search widgets"
              aria-describedby="widget-gallery-help"
              data-testid="widget-gallery-search"
              placeholder="Search…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="pl-7 h-9 text-xs"
            />
          </div>
          <NativeSelect
            aria-label="Category"
            data-testid="widget-gallery-category"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="w-full h-9 text-xs"
          >
            <option value="">All categories</option>
            {CATEGORY_ORDER.map((c) => (
              <option key={c} value={c}>
                {CATEGORY_LABEL[c] ?? c}
              </option>
            ))}
          </NativeSelect>
          <NativeSelect
            aria-label="Module"
            data-testid="widget-gallery-module"
            value={module}
            onChange={(e) => setModule(e.target.value)}
            className="w-full h-9 text-xs"
          >
            <option value="">All modules</option>
            {moduleOptions.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </NativeSelect>
          <NativeSelect
            aria-label="Persona"
            data-testid="widget-gallery-persona"
            value={persona}
            onChange={(e) => setPersona(e.target.value)}
            className="w-full h-9 text-xs"
          >
            <option value="">All personas</option>
            {personaOptions.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </NativeSelect>
          <NativeSelect
            aria-label="Industry"
            data-testid="widget-gallery-industry"
            value={industry}
            onChange={(e) => setIndustry(e.target.value)}
            className="w-full h-9 text-xs"
          >
            <option value="">All industries</option>
            {industryOptions.map((i) => (
              <option key={i} value={i}>
                {i}
              </option>
            ))}
          </NativeSelect>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-auto pr-1">
          {loading && (
            <p className="text-xs text-muted-foreground p-4">Loading widget catalogue…</p>
          )}
          {error && (
            <p className="text-xs text-alarm-critical p-4" role="alert">
              {error}
            </p>
          )}
          {!loading && !error && filtered.length === 0 && (
            <p className="text-xs text-muted-foreground p-4">
              No widgets match the current filters.
            </p>
          )}
          {flatWidgets.length > 0 && (
            <div
              role="listbox"
              aria-label="Widget results"
              aria-describedby="widget-gallery-help"
              tabIndex={-1}
              onKeyDown={handleGridKeyDown}
            >
              {grouped.map(([key, widgets]) => (
                <section key={key} className="mb-4">
                  <h3 className="t-eyebrow mb-2" id={`widget-cat-${key}`}>
                    {CATEGORY_LABEL[key] ?? key}
                  </h3>
                  <div
                    role="group"
                    aria-labelledby={`widget-cat-${key}`}
                    className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2"
                  >
                    {widgets.map((w) => {
                      const flatIndex = flatWidgets.indexOf(w);
                      return (
                        <WidgetCard
                          key={w.widgetId}
                          widget={w}
                          onAdd={() => onAdd(w)}
                          active={flatIndex === activeIndex}
                          onFocus={() => setActiveIndex(flatIndex)}
                          cardRef={(el) => {
                            cardRefs.current[flatIndex] = el;
                          }}
                        />
                      );
                    })}
                  </div>
                </section>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const SIZE_PREVIEW: Record<string, string> = {
  small: 'col-span-1',
  medium: 'col-span-2',
  large: 'col-span-3',
  extraLarge: 'col-span-4',
  wallboard: 'col-span-6',
};

const WidgetCard: React.FC<{
  widget: WidgetDefinition;
  onAdd: () => void;
  active: boolean;
  onFocus: () => void;
  cardRef: (el: HTMLButtonElement | null) => void;
}> = ({ widget, onAdd, active, onFocus, cardRef }) => {
  const defaultSize = widget.defaultSize ?? widget.supportedSizes?.[0] ?? 'medium';
  const categoryLabel = CATEGORY_LABEL[(widget.category ?? 'other').toLowerCase()] ?? 'Other';
  return (
    <Button
      type="button"
      variant="ghost"
      ref={cardRef}
      role="option"
      aria-selected={active}
      tabIndex={active ? 0 : -1}
      onFocus={onFocus}
      onClick={onAdd}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onAdd();
        }
      }}
      aria-label={`${widget.label}, ${categoryLabel}, default size ${defaultSize}. ${
        widget.description ?? 'No description provided.'
      }. Press Enter to add to dashboard.`}
      data-testid={`widget-card-${widget.widgetId}`}
      className="h-auto whitespace-normal justify-start font-normal group glass block w-full text-left rounded-2xl border border-border p-3 transition-all hover:border-primary/40 hover:shadow-[var(--shadow-overlay)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 aria-selected:border-primary/60 aria-selected:ring-1 aria-selected:ring-primary/40"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-start gap-2 min-w-0">
          <span
            className="flex h-8 w-8 items-center justify-center rounded-xl bg-primary/10 text-primary shrink-0"
            aria-hidden="true"
          >
            <LayoutGrid className="h-4 w-4" />
          </span>
          <div className="min-w-0">
            <p className="text-sm font-medium text-foreground truncate">{widget.label}</p>
            <p className="t-mono text-[10px] text-muted-foreground/70 truncate">
              {widget.widgetId}
            </p>
          </div>
        </div>
      </div>
      <p className="mt-2 text-xs text-muted-foreground line-clamp-2 min-h-[2.5rem]">
        {widget.description ?? 'No description provided.'}
      </p>

      {/* Size preview — illustrative bars showing the supported sizes. */}
      <div
        aria-hidden="true"
        className="mt-2 grid grid-cols-6 gap-0.5 h-1.5 rounded bg-muted overflow-hidden"
      >
        <span className={`${SIZE_PREVIEW[defaultSize] ?? 'col-span-2'} h-full bg-primary/60`} />
      </div>
      <p className="mt-1 text-[10px] text-muted-foreground">
        Default size: <span className="t-mono">{defaultSize}</span>
      </p>

      {/* Visual "Add" affordance — the whole card is the actionable control
          (role=option, Enter/click adds), so this is a decorative cue, not a
          nested interactive element (which would be invalid inside a button). */}
      <div className="mt-2 flex justify-end">
        <span
          data-testid={`widget-card-add-${widget.widgetId}`}
          aria-hidden="true"
          className="inline-flex items-center rounded-md bg-primary px-2.5 py-1 text-xs font-medium text-primary-foreground transition-colors group-hover:bg-primary/90"
        >
          <Plus className="h-3 w-3 mr-1" aria-hidden="true" />
          Add
        </span>
      </div>
    </Button>
  );
};

export default WidgetGallery;
